import { useState, useEffect, useCallback } from 'react';
import { ButtonField, InputField, Modal, ModalHeader, ModalBody, ModalFooter, ButtonGroup, ButtonGroupItem, CheckboxField, SelectField, Notification } from '@wk/components-react16';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../../config/constants';
import HttpService from "../../config/http.service";
import { Toast } from "../../services"
import { RootState } from '../../store';
import { useSelector, useDispatch } from 'react-redux';
import { showLoading, hideLoading } from "../../store/authReducer";

const AddValidation = ({ data }: any) => {
    const { isOpenValidationRule, setIsOpenValidationRule, validationRule, setValidationRule, setIsLoad, validationObj } = data;
    const navigate = useNavigate();
    const location = useLocation();
	const dispatch = useDispatch();
    const { authProject } = useSelector((state: RootState) => state.auth);
    const [promptList, setPromptList] = useState([] as any);
    const [facets, setFacets] = useState([] as any);
    const [isSelectAllPrompts, setisSelectAllPrompts] = useState(false);

    const getPrompts = useCallback(() => {
        if (!authProject?.id) {
            return;
        }

        dispatch(showLoading());
        let payload = {
            projectId: authProject.id,
            page: 1,
            size: 10000,
            filterText: '',
            promptType: [],
            createdBy: [],
            catIds: [],
            data: true,
            searchField:"name"
        }
        HttpService.postRequest(`${API_URL.POST_ALL_PROMPTS}`, payload).then(
            (res: any) => {
                let list = res?.data?.map((item: any) => ({ ...item, isSelected: false, isVisible: true }));
                if(validationRule.prompts.length > 0) {
                    list.forEach((item: any) => {
                        item.isSelected = validationRule.prompts.some((itm: any) => itm === item.id);
                    });
                }
                setPromptList(list);
                let promptTypes = res?.facets.find((facet: any) => facet.name === "Prompt Type")
                setFacets(promptTypes.children || []);
                dispatch(hideLoading());
            },
            (err: any) => {
                Toast(err.message, "error");
                dispatch(hideLoading());
            },
        )
    }, [authProject?.id, validationRule.prompts, dispatch])

    const handleUserRequest = (origin: string) => {
        if (origin === 'esc' || origin === 'header' || origin === 'footer') {
            setValidationRule(validationObj);
            setIsOpenValidationRule(false);
        }
    }

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setValidationRule({
            ...validationRule,
            [name]: value,
        });
    };

    const isValidInput = (rule: any) => {
        if (!rule.name || !rule.name.trim()) {
            Toast('Please enter validation name', "error");
            return false;
        }
        if (!rule.text || !rule.text.trim()) {
            Toast('Please enter validation text', "error");
            return false;
        }
        return true;
    }

    const saveValidationRule = () => {
        if (!isValidInput(validationRule)) return;
        dispatch(showLoading());
        const prompts = promptList.filter((item: any) => item.isSelected === true).map((item: any) => item.id);
        const payload: any = {
            projectId: authProject.id,
            id: validationRule.id,
            name: validationRule.name,
            text: validationRule.text,
            prompts: prompts.length > 0 ? prompts : null
        }

        HttpService.postRequest(API_URL.ADD_VALIDATION, payload).then(
            (res: any) => {
                dispatch(hideLoading());
                Toast(validationRule.id > 0 ? 'Validation rule updated successfully.' : 'Validation rule added successfully.', "success");
                setIsLoad(true);
                setValidationRule(validationObj);
                setIsOpenValidationRule(false);
                if (location.pathname !== '/validationrule') navigate("/validationrule");
            },
            (err: any) => {
                dispatch(hideLoading());
                Toast(err.message, "error");
            }
        )
    }

    const onPromptSelect = (index: number) => {
        const updatedList = [...promptList];
        updatedList[index].isSelected = !updatedList[index].isSelected;
        setPromptList(updatedList);
        calculateSelectedPrompts(updatedList);
    }

    const onSelectAllPrompt = (event: any) => {
        setisSelectAllPrompts(event.target.checked);
        const updatedList = promptList.map((item: any) => ({ ...item, isSelected: item.isVisible ? event.target.checked : item.isSelected }));
        setPromptList(updatedList);
    }

    const filterByPromptType = (event: any) => {
        const value = event.target.value;
        const updatedList = promptList.map((item: any) => {
            if (value === "") {
                return { ...item, isVisible: true };
            } else {
                return { ...item, isVisible: item.promptType && item.promptType.id.toString() === value ? true : false };
            }
        });
        setPromptList(updatedList);
        calculateSelectedPrompts(updatedList);
    }

    const calculateSelectedPrompts = (updatedList: any[]) => {
        const visibleItems = updatedList.filter((item: any) => item.isVisible);
        const allVisibleSelected = visibleItems.length > 0 && visibleItems.every((item: any) => item.isSelected);
        setisSelectAllPrompts(allVisibleSelected);
    }

    const getSelectedPromptsCount = () => {
        return promptList.filter((item: any) => item.isSelected === true).length;
    }

    useEffect(() => {
        getPrompts();
    }, [getPrompts]);

    return (
        <div className="custom-modal-body-overflow" data-component="AddValidation">
            <Modal
                isOpen={isOpenValidationRule}
                srLabelledBy={'modal-id-005'}
                srDescribedBy={'modal-id-006'}
                width={'600px'}
                onUserRequest={handleUserRequest}>
                <ModalHeader
                    titleId={'modal-id-005'}
                    isFullscreenButtonVisible
                    onUserRequest={() => handleUserRequest('header')}>
                    Validation Rule
                </ModalHeader>
                <ModalBody descriptionId="modal-validationrule-1">
                    <Notification type="info" heading="Note" autoCloseTimeout={0}>
                        <span slot="notificationContent">
                            A validation rule must include only one instruction; multiple instructions require multiple validation rules. You can assign single rule to multiple prompts or multiple rules to single prompts.
                        </span>
                    </Notification>
                    <div className="wk-row">
                        <div className="wk-col-12 custom-overflow-inherit">
                            <InputField label="Validation Name" labelFor="validation-name-1" size="small" indicator="required">
                                <input
                                    type="text" id="validation-name-1" name="name"
                                    placeholder="Validation Name"
                                    onChange={(event: any) => handleInputChange(event)}
                                    value={validationRule.name} autoComplete="off"
                                />
                            </InputField>
                            <InputField label="Validation Description" labelFor="validation-text-1" size="small" indicator="required">
                                <input
                                    type="text" id="validation-text-1" name="text"
                                    placeholder="Validation Description"
                                    onChange={(event: any) => handleInputChange(event)}
                                    value={validationRule.text} autoComplete="off"
                                />
                            </InputField>
                            {/* <Multiselect
                                items={promptList}
                                pillLabel="Prompt assigned"
                                placeholder="Select Prompt"
                                size="small"
                                label="Prompts"></Multiselect> */}

                            <div className="flex space-between align-center">
                                <div>
                                    <p className="mb-0 bold">Select Prompts ({getSelectedPromptsCount()})</p>
                                </div>
                                <div className='custom-field-mb-5'>
                                    <SelectField size='small'>
                                        <select name="type"
                                            onChange={(event: any) => filterByPromptType(event)}
                                            //value={prompt.promptType?.id || ''}
                                        >
                                            <option value="">-- Filter By Prompt Type --</option>
                                            {facets.map((item: any, i: number) => <option value={item.id} key={i}>{item.name}</option>)}
                                        </select>
                                    </SelectField>
                                </div>
                                
                            </div>
                            <div style={{height: '150px', overflowY: 'auto', position: 'relative'}}>
                            <table className="wk-table wk-table-bordered wk-table-small">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>
                                            <CheckboxField label="">
                                            <input type="checkbox" 
                                                checked={isSelectAllPrompts}
                                                onChange={(event:any) => {onSelectAllPrompt(event);}}
                                            />
                                            </CheckboxField>
                                        </th>
                                        <th>Prompt Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promptList.length > 0 ? promptList.map((item: any, index: number) => (
                                        item.isVisible === false ? null : (
                                            <tr key={index}>
                                                <td>
                                                    <CheckboxField label="">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.isSelected}
                                                            onChange={() => {onPromptSelect(index);}}
                                                        />
                                                    </CheckboxField>
                                                </td>
                                                <td>{item.name}</td>
                                                
                                            </tr>
                                        )
                                    )) : (
                                        <tr>
                                            <td colSpan={2} className="text-center">No prompts available.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup mode="static">
                        <ButtonGroupItem slot="buttonGroupItem">
                            <ButtonField size={'small'} mode={'text'}>
                                <button type="button" id="cancel" onClick={() => handleUserRequest('footer')}>
                                    Cancel
                                </button>
                            </ButtonField>
                        </ButtonGroupItem>
                        <ButtonGroupItem slot="buttonGroupItem">
                            <ButtonField size={'small'}>
                                <button type="button" id="save" onClick={() => saveValidationRule()}>
                                    Save
                                </button>
                            </ButtonField>
                        </ButtonGroupItem>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default AddValidation;
